import React, { useState, useRef, useEffect } from 'react';
import { useSpc } from '../context/SpcContext';
import { Bell, ShoppingBag, CalendarClock, Sparkles, Check, Gamepad2, ClipboardList, Target, UserCheck, MessageSquare, Settings, Award, Megaphone, Zap, ShieldAlert, Heart, Users, Gift } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const ICONS = {
  kit: <ShoppingBag size={16} />,
  message: <MessageSquare size={16} />,
  session: <CalendarClock size={16} />,
  booking: <CalendarClock size={16} />,
  session_reminder: <CalendarClock size={16} />,
  session_reminder_2h: <CalendarClock size={16} />,
  safety_card_reminder: <ShieldAlert size={16} />,
  points: <Sparkles size={16} />,
  game: <Gamepad2 size={16} />,
  poll: <ClipboardList size={16} />,
  challenge: <Target size={16} />,
  attendance: <UserCheck size={16} />,
  apex: <ShoppingBag size={16} />,
  announcement: <Sparkles size={16} />,
  admin: <Sparkles size={16} />,
  medal_monday: <Award size={16} />,
  breaking_news: <Megaphone size={16} />,
  random_act: <Zap size={16} />,
  feed_like: <Heart size={16} />,
  feed_reaction: <Heart size={16} />,
  feed_comment: <Users size={16} />,
  feed_mention: <Users size={16} />,
  member: <UserCheck size={16} />,
  reward: <Gift size={16} />,
};

// Maps a backend notification type to an in-app view id used by the Shell router.
// `messages` is handled specially (opens the messages modal).
const NOTIF_ROUTE = {
  kit: 'community',
  message: 'community',
  session: 'book',
  booking: 'book',
  session_reminder: 'book',
  session_reminder_2h: 'book',
  safety_card_reminder: 'profile',
  points: 'profile',
  game: 'games',
  poll: 'coachhub',
  challenge: 'challenges',
  attendance: 'profile',
  apex: 'shop',
  announcement: 'home',
  admin: 'profile',
  medal_monday: 'home',
  random_act: 'home',
  feed_like: 'feed',
  feed_reaction: 'feed',
  feed_comment: 'feed',
  feed_mention: 'feed',
  member: 'profile',
  reward: 'profile',
};

export default function Notifications() {
  const { state, markNotificationsRead, clearNotifications, enablePush, navigateTo, openModal, focusSession } = useSpc();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const list = state.notifications || [];
  const unread = list.filter(n => !n.read).length;
  const hasNotifAPI = typeof Notification !== 'undefined';
  const permission = hasNotifAPI ? Notification.permission : null;
  const isStandalone = typeof window !== 'undefined' && (window.navigator.standalone || window.matchMedia?.('(display-mode: standalone)').matches);
  const isIOS = typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && unread) setTimeout(() => markNotificationsRead(), 1200);
  };

  const handleClick = (n) => {
    setOpen(false);
    // Messages are threaded — open the messages modal instead of navigating.
    if (n.type === 'message') {
      openModal('messages');
      return;
    }
    // Admin's "new challenge entry" notification should land directly on the review tab
    // (with that submission ready to approve/reject), not just the generic Challenges page.
    if (n.type === 'challenge' && (n.title || '').toLowerCase() === 'new challenge entry') {
      openModal('adminChallenge', { tab: 'review' });
      return;
    }
    // Breaking News is a one-off popup, not a persistent page — show its own content back
    // (title/body are stored on the notification itself) rather than sending them to Home
    // with nothing there to explain what it was about.
    if (n.type === 'breaking_news') {
      openModal('breakingNewsDetail', { title: n.title, body: n.body });
      return;
    }
    // Safety Card nudge should land directly on the form to fill in, not just the profile page.
    if (n.type === 'safety_card_reminder') {
      navigateTo('profile');
      openModal('safetyCard');
      return;
    }
    // New signup — take the admin straight to the Manage Members tool to review/approve.
    if (n.type === 'member') {
      navigateTo('profile');
      openModal('adminMembers');
      return;
    }
    // A member cashed in points — take the admin straight to the redemptions tool to fulfil it.
    if (n.type === 'reward') {
      navigateTo('profile');
      openModal('adminRedemptions');
      return;
    }
    // A notification can carry its own specific destination (e.g. an announcement's chosen
    // link or an admin-pasted external URL) — that always wins over the generic type-based
    // fallback below, since it points to exactly what the notification was about.
    if (n.url) {
      if (/^https?:\/\//i.test(n.url)) {
        window.open(n.url, '_blank', 'noopener,noreferrer');
      } else {
        // A specific session id (e.g. "New Social booking", a reminder, or an updated/new
        // session) — jump straight to THAT session on the Book page (scrolls to it, flips
        // the calendar to its date and flashes it), not just the Book tab in general.
        const sidMatch = n.url.match(/[?&]sid=([a-zA-Z0-9-]+)/);
        if (sidMatch) { focusSession(sidMatch[1]); return; }
        const match = n.url.match(/nav=([a-zA-Z]+)/);
        if (match) navigateTo(match[1]);
      }
      return;
    }
    const view = NOTIF_ROUTE[n.type];
    if (view) navigateTo(view);
  };

  return (
    <div className="notif-wrap" ref={wrapRef}>
      <button className="notif-btn" data-testid="notif-bell-btn" onClick={toggle} aria-label="Notifications">
        <Bell size={19} />
        {unread > 0 && <span className="notif-badge" data-testid="notif-badge">{unread}</span>}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div className="notif-panel" initial={{ opacity: 0, y: -8, scale: .98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -8, scale: .98 }} transition={{ duration: .2 }}>
            <div className="notif-head">
              <div><strong>Notifications</strong><span>{unread ? `${unread} new` : 'All caught up'}</span></div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="notif-clear" data-testid="notif-prefs-btn" onClick={() => { setOpen(false); openModal('notificationPrefs'); }} aria-label="Notification settings"><Settings size={14} /></button>
                {hasNotifAPI && permission === 'default' && <button className="notif-clear" data-testid="enable-push-btn" onClick={enablePush}>Enable push</button>}
                {!hasNotifAPI && isIOS && !isStandalone && <button className="notif-clear" data-testid="ios-push-hint-btn" onClick={() => openModal('iosInstall')}>Push needs the app installed →</button>}
                {list.length > 0 && <button className="notif-clear" data-testid="notif-clear-btn" onClick={clearNotifications}>Clear</button>}
              </div>
            </div>
            {hasNotifAPI && permission === 'denied' && <div className="notif-push-note-row" data-testid="push-denied-note">{isIOS ? 'Notifications blocked — enable in iPhone Settings → Notifications → SPC Clubhouse' : 'Notifications blocked — enable them for this site in your browser settings'}</div>}
            {!hasNotifAPI && isIOS && isStandalone && <div className="notif-push-note-row" data-testid="ios-update-note">Update iOS to 16.4+ for push notifications</div>}
            <div className="notif-list">
              {list.length === 0 && <div className="notif-empty"><Check size={22} /><p>You're all caught up.</p></div>}
              {list.map(n => (
                <button
                  type="button"
                  className={`notif-item notif-item-btn ${n.read ? '' : 'unread'}`}
                  key={n.id}
                  data-testid="notif-item"
                  onClick={() => handleClick(n)}
                >
                  <span className={`notif-icon ${n.type}`}>{ICONS[n.type] || <Bell size={16} />}</span>
                  <div className="notif-text">
                    <strong>{n.title}</strong>
                    <p>{n.body}</p>
                    <span className="notif-time">{n.time}</span>
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
